import { Decoder, Stream, Profile } from "@garmin/fitsdk";
import type { Mesg } from "@garmin/fitsdk";
import type {
  RideData,
  RecordPoint,
  OrderedMesg,
  FieldDescription,
} from "./types";

const SEMICIRCLE_TO_DEG = 180 / 2 ** 31;

/** Convert a FIT semicircle position value to degrees, or null. */
function toDegrees(semicircles: unknown): number | null {
  return typeof semicircles === "number" && Number.isFinite(semicircles)
    ? semicircles * SEMICIRCLE_TO_DEG
    : null;
}

/** Read a numeric field, preferring the first defined of several aliases. */
function num(mesg: Record<string, unknown>, ...keys: string[]): number | null {
  for (const key of keys) {
    const v = mesg[key];
    if (typeof v === "number" && Number.isFinite(v)) return v;
  }
  return null;
}

/** Epoch-ms of a decoded FIT timestamp (a Date once convertDateTimesToDates is on). */
function timeMs(v: unknown): number | null {
  if (v instanceof Date) return v.getTime();
  return null;
}

/** Find the index of the record whose time is closest to `t` (records sorted by time). */
export function nearestIndexByTime(records: RecordPoint[], t: number): number {
  let lo = 0;
  let hi = records.length - 1;
  if (t <= records[lo].time) return lo;
  if (t >= records[hi].time) return hi;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (records[mid].time < t) lo = mid;
    else hi = mid;
  }
  return t - records[lo].time <= records[hi].time - t ? lo : hi;
}

export class FitParseError extends Error {}

/**
 * Decode a .fit file into the app's {@link RideData}, retaining the original
 * message stream (in file order) and developer-field descriptions so the file
 * can later be re-encoded with rebuilt laps.
 */
export function decodeFit(fileName: string, buffer: ArrayBuffer): RideData {
  const stream = Stream.fromArrayBuffer(buffer);
  const decoder = new Decoder(stream);

  if (!decoder.isFIT()) {
    throw new FitParseError("That doesn't look like a .fit file.");
  }
  if (!decoder.checkIntegrity()) {
    throw new FitParseError(
      "The .fit file failed its integrity check (header or CRC).",
    );
  }

  const ordered: OrderedMesg[] = [];
  const { messages, errors } = decoder.read({
    // Capture every message in original file order — required to preserve
    // interleaving when we re-encode.
    mesgListener: (mesgNum: number, mesg: Mesg) => {
      ordered.push({ mesgNum, mesg: mesg as unknown as Record<string, unknown> });
    },
  });

  if (errors.length > 0) {
    throw new FitParseError(errors[0].message || "Failed to decode the file.");
  }

  const recordMesgs = (messages.recordMesgs ?? []) as unknown as Record<
    string,
    unknown
  >[];
  if (recordMesgs.length === 0) {
    throw new FitParseError("No record (GPS/telemetry) data found in the file.");
  }

  // Build normalised records, sorted by time.
  const records: RecordPoint[] = [];
  for (const r of recordMesgs) {
    const time = timeMs(r.timestamp);
    if (time === null) continue;
    records.push({
      index: 0,
      time,
      lat: toDegrees(r.positionLat),
      lng: toDegrees(r.positionLong),
      distance: num(r, "distance"),
      altitude: num(r, "enhancedAltitude", "altitude"),
      speed: num(r, "enhancedSpeed", "speed"),
      power: num(r, "power"),
      heartRate: num(r, "heartRate"),
      cadence: num(r, "cadence"),
      temperature: num(r, "temperature"),
    });
  }
  records.sort((a, b) => a.time - b.time);
  records.forEach((r, i) => (r.index = i));

  // Some rides record a `distance` that is not globally monotonic — it jumps
  // back toward zero partway through (multi-segment activities, device/lap
  // distance resets). Rebuild a monotonic cumulative distance so the distance
  // axis and lap distances stay sensible instead of the trace doubling back.
  {
    let cum = 0;
    let prevRaw: number | null = null;
    for (const r of records) {
      if (r.distance === null) continue;
      const raw = r.distance;
      if (prevRaw === null) {
        cum = raw;
      } else {
        const delta = raw - prevRaw;
        if (delta >= 0) cum += delta;
        else if (raw < prevRaw - 50) cum += raw; // reset: a new segment from ~0
        // else: small backward GPS noise — hold distance steady
      }
      prevRaw = raw;
      r.distance = cum;
    }
  }

  const hasPosition = records.some((r) => r.lat !== null && r.lng !== null);
  const hasAltitude = records.some((r) => r.altitude !== null);

  const startTime = records[0].time;
  const endTime = records[records.length - 1].time;

  // Developer-field descriptions, keyed by field key (needed to re-encode files
  // that carry app-specific developer fields, e.g. Wahoo/Garmin extensions).
  const fieldDescriptions: Record<string, FieldDescription> = {};
  const devIds = (messages.developerDataIdMesgs ?? []) as unknown as Record<
    string,
    unknown
  >[];
  for (const fd of (messages.fieldDescriptionMesgs ?? []) as unknown as Record<
    string,
    unknown
  >[]) {
    const devId = devIds.find(
      (d) => d.developerDataIndex === fd.developerDataIndex,
    );
    if (devId && fd.key != null) {
      fieldDescriptions[String(fd.key)] = {
        developerDataIdMesg: devId,
        fieldDescriptionMesg: fd,
      };
    }
  }

  // Initial lap boundaries: map each source lap's end to the nearest record.
  const lapMesgs = (messages.lapMesgs ?? []) as unknown as Record<
    string,
    unknown
  >[];
  lapMesgs.sort((a, b) => (timeMs(a.startTime) ?? 0) - (timeMs(b.startTime) ?? 0));

  const initialSplits: number[] = [];
  for (let i = 0; i < lapMesgs.length - 1; i++) {
    const end = timeMs(lapMesgs[i].timestamp);
    if (end === null) continue;
    const idx = nearestIndexByTime(records, end);
    if (idx > 0 && idx < records.length - 1) initialSplits.push(idx);
  }
  const uniqueSplits = [...new Set(initialSplits)].sort((a, b) => a - b);

  const session = (messages.sessionMesgs ?? [])[0] as unknown as
    | Record<string, unknown>
    | undefined;
  const lastLap = lapMesgs[lapMesgs.length - 1];
  const sportSource = session ?? lastLap ?? {};

  const totalDistance =
    num(sportSource, "totalDistance") ??
    records[records.length - 1].distance ??
    null;

  return {
    fileName,
    records,
    hasPosition,
    hasAltitude,
    startTime,
    endTime,
    totalDistance,
    initialSplits: uniqueSplits,
    ordered,
    fieldDescriptions,
    lastLapTrigger: lastLap?.lapTrigger as string | number | undefined,
    sport: sportSource.sport as string | number | undefined,
    subSport: sportSource.subSport as string | number | undefined,
  };
}

/** Numeric global message numbers we care about, from the FIT profile. */
export const MesgNum = {
  SESSION: Profile.MesgNum.SESSION,
  LAP: Profile.MesgNum.LAP,
  RECORD: Profile.MesgNum.RECORD,
} as const;
